import React, { useState } from 'react';
import { Bookmark, ArrowRight, Trash2, RefreshCw } from 'lucide-react';
import Tooltip from '../../../../../components/ui/Tooltip';

/**
 * Компонент для отображения сохраненных конвертаций
 * 
 * @param {Array} savedConversions - Массив сохраненных конвертаций
 * @param {Object} currencies - Объект с информацией о валютах
 * @param {Function} deleteConversion - Функция для удаления сохраненной конвертации
 * @param {Function} setFormData - Функция для установки данных формы
 */
const SavedConversions = ({ savedConversions, currencies, deleteConversion, setFormData }) => {
  // Состояние для количества отображаемых конвертаций
  const [showCount, setShowCount] = useState(3);
  
  // Если нет сохраненных конвертаций, не показываем компонент
  if (!savedConversions || savedConversions.length === 0) {
    return null;
  }
  
  // Функция для форматирования даты
  const formatDate = (dateStr) => {
    const date = new Date(dateStr);
    return date.toLocaleString('ru-RU', {
      day: 'numeric',
      month: 'short',
      hour: '2-digit',
      minute: '2-digit'
    });
  };
  
  // Функция для загрузки сохраненной конвертации
  const loadConversion = (conv) => {
    setFormData({
      amount: conv.fromAmount.toString(),
      fromCurrency: conv.fromCurrency,
      toCurrency: conv.toCurrency
    });
    
    // Прокручиваем страницу вверх к форме конвертера
    window.scrollTo({
      top: 0,
      behavior: 'smooth'
    });
  };
  
  // Получаем ограниченное количество конвертаций
  const visibleConversions = savedConversions.slice(-showCount).reverse();
  
  return (
    <div className="bg-white dark:bg-gray-800 rounded-xl shadow-md border border-gray-200 dark:border-gray-700 p-6 mb-8">
      <h2 className="text-xl font-semibold text-gray-900 dark:text-white mb-6 flex items-center">
        <Bookmark className="mr-2 h-6 w-6 text-indigo-500" />
        Сохраненные конвертации
      </h2>
      
      <div className="space-y-4">
        {visibleConversions.map(conv => (
          <div 
            key={conv.id} 
            className="flex flex-col sm:flex-row items-start sm:items-center justify-between p-4 bg-gray-50 dark:bg-gray-700/50 rounded-lg hover:shadow-md transition-shadow"
          >
            <div className="flex items-center flex-grow mb-3 sm:mb-0" onClick={() => loadConversion(conv)}>
              <div className="cursor-pointer hover:bg-gray-100 dark:hover:bg-gray-600 p-2 rounded-md">
                <div className="flex items-center">
                  <div className="flex items-center">
                    <span className="font-medium text-gray-900 dark:text-white">
                      {conv.fromAmount.toLocaleString()}
                    </span>
                    <span className="mx-1 text-gray-600 dark:text-gray-400">
                      {conv.fromCurrency}
                    </span>
                  </div>
                  
                  <ArrowRight className="h-4 w-4 mx-2 text-gray-400" />
                  
                  <div className="flex items-center">
                    <span className="font-medium text-gray-900 dark:text-white">
                      {conv.toAmount.toLocaleString(undefined, { maximumFractionDigits: 4 })}
                    </span>
                    <span className="mx-1 text-gray-600 dark:text-gray-400">
                      {conv.toCurrency}
                    </span>
                  </div>
                </div>
                
                <div className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                  {formatDate(conv.date)} • Курс: {conv.rate.toFixed(4)}
                </div>
              </div>
            </div>
            
            <div className="flex space-x-2">
              <Tooltip text="Применить">
                <button
                  type="button"
                  onClick={() => loadConversion(conv)}
                  className="p-2 rounded-md text-gray-600 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-600 hover:text-indigo-600 dark:hover:text-indigo-400"
                  aria-label="Применить конвертацию"
                >
                  <RefreshCw className="h-5 w-5" />
                </button>
              </Tooltip>
              
              <Tooltip text="Удалить">
                <button
                  type="button"
                  onClick={() => deleteConversion(conv.id)}
                  className="p-2 rounded-md text-gray-600 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-600 hover:text-red-600 dark:hover:text-red-400"
                  aria-label="Удалить конвертацию"
                >
                  <Trash2 className="h-5 w-5" />
                </button>
              </Tooltip>
            </div>
          </div>
        ))}
      </div>
      
      {/* Кнопка "Показать больше" */}
      {savedConversions.length > showCount && (
        <div className="mt-4 text-center">
          <button
            type="button"
            onClick={() => setShowCount(prev => prev + 3)}
            className="text-indigo-600 dark:text-indigo-400 hover:text-indigo-800 dark:hover:text-indigo-300 text-sm font-medium"
          >
            Показать больше
          </button>
        </div>
      )}
      
      {/* Кнопка "Показать меньше" */}
      {showCount > 3 && showCount > savedConversions.length && (
        <div className="mt-4 text-center">
          <button
            type="button"
            onClick={() => setShowCount(3)}
            className="text-indigo-600 dark:text-indigo-400 hover:text-indigo-800 dark:hover:text-indigo-300 text-sm font-medium"
          >
            Показать меньше
          </button>
        </div>
      )}
    </div>
  );
};

export default SavedConversions;